import {useEffect, useMemo, useRef} from 'react'
import {Spinner, Timeline} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import {CommentIcon} from '@primer/octicons-react'
import {useInfiniteTimelinePageData} from '../../../page-data/loaders/use-timeline'
import {usePullRequestBasePath} from '../../../stores/selectors'
import {TimelineEvent} from './TimelineEvent'
import styles from './TimelinePanel.module.css'

export function TimelinePanelContent() {
  const pathName = usePullRequestBasePath()
  const {data, fetchNextPage, hasNextPage, isFetchingNextPage} = useInfiniteTimelinePageData({pathName})
  const items = useMemo(() => data.pages.flatMap(page => page.data), [data.pages])
  const sentinelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage()
      },
      {threshold: 0.1},
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  if (items.length === 0 && !isFetchingNextPage) {
    return (
      <div className={styles.emptyState}>
        <Blankslate>
          <Blankslate.Visual>
            <CommentIcon size={24} />
          </Blankslate.Visual>
          <Blankslate.Heading>No timeline events</Blankslate.Heading>
          <Blankslate.Description>Activity on this pull request will appear here.</Blankslate.Description>
        </Blankslate>
      </div>
    )
  }

  return (
    <>
      <Timeline clipSidebar="end" className={styles.timelineList}>
        {items.map(item => (
          <TimelineEvent key={item.id} item={item} />
        ))}
      </Timeline>
      {hasNextPage && <div ref={sentinelRef} aria-hidden className={styles.sentinel} />}
      {isFetchingNextPage && (
        <div role="status" className={styles.loadingMore}>
          <Spinner size="small" />
        </div>
      )}
    </>
  )
}
